import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import UserManagementService from '../services/firebase/userManagementService';
import profileService from '../services/profileService';
import api, { businessesAPI } from '../shared/services/api';
import { initFCMForUser, requestNotificationPermission, setupForegroundNotifications, removeFCMToken } from '../services/fcmService';
import { isAdminUser } from '../utils/userUtils';
import { db, auth } from '../services/firebase';
import { getDoc, doc, query, where, collection, getDocs, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { hibritService } from '../services/hibritService'; // HibritService eklendi
import { getPlatformInfo, getOrCreateDeviceId, initAppLifecycleListener } from '../utils/deviceHelper';
import safeStorage from '../utils/safeStorage';

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_API_URL || 'http://localhost:5005');

const AuthBusinessContext = createContext();

// 🇹🇷 LOKALİZASYON HARİTASI
const SECTOR_LOCALIZATION = {
  'food-beverage': 'Restoran & Kafe',
  'food_beverage': 'Restoran & Kafe',
  'food': 'Restoran & Kafe',
  'restaurant': 'Restoran & Kafe',
  'retail': 'Perakende Mağaza',
  'hospitality': 'Otel & Konaklama',
  'hotel': 'Otel & Konaklama',
  'service': 'Hizmet İşletmesi',
  'bakery': 'Pastane & Fırın',
  'pizzeria': 'Pizzacı',
  'cloud-kitchen': 'Bulut Mutfak',
  'cafe': 'Kafe & Bistro'
};

const ROLE_LOCALIZATION = {
  'owner': 'İşletme Sahibi',
  'business_owner': 'İşletme Sahibi',
  'admin': 'Yönetici',
  'manager': 'Müdür',
  'staff': 'Personel',
  'waiter': 'Garson',
  'chef': 'Aşçı',
  'courier': 'Kurye',
  'system_admin': 'Sistem Yöneticisi',
  'super_admin': 'Süper Yönetici'
};

export const useAuthBusiness = () => {
  const context = useContext(AuthBusinessContext);
  if (!context) {
    throw new Error('useAuthBusiness must be used within AuthBusinessProvider');
  }
  return context;
};

export const useAuthBusinessContext = useAuthBusiness;

// 🎯 SIDEBAR VISIBILITY LOGIC
const determineSidebarVisibility = (user, selectedBiz) => {
  if (!user?.id && !user?.email) return false;

  // 🛡️ CARI CHECK: Cari users should never see sidebar
  const isCari = user?.role === 'cari' || user?.user_type === 'cari' || selectedBiz?.role === 'cari';
  if (isCari) return false;

  // SaaS Unified: Member users with memberships should see sidebar in business/sectoral contexts
  if (user?.type === 'human' && user?.memberships?.length > 0) {
    return true;
  }

  // Admin users always show sidebar
  if (user?.user_type === 'system_admin' || user?.user_type === 'admin' ||
    user?.role === 'super_admin' || user?.role === 'system_admin' ||
    user?.email === 'admin@prmanager.com') {
    return true;
  }

  // Branch manager users always show sidebar
  if (user?.role === 'branch_manager' || user?.user_type === 'branch_manager' || user?.is_branch_account) {
    return true;
  }

  switch (user?.user_type) {
    case 'business_owner':
    case 'branch_manager':
    case 'human': // Add human here as well
      return true; 
    case 'brand_employee':
      return !!(selectedBiz && selectedBiz.id);
    default:
      return user?.role === 'branch_manager' || Boolean(user?.is_branch_account);
  }
};

// 🎯 NORMALIZED SECTOR DETECTOR
export const getNormalizedSector = (business) => {
  if (!business) return 'food-beverage';
  
  const rawSector = (business.sector || business.business_type || '').toLowerCase();
  const category = (business.category_name || business.category || '').toLowerCase();
  
  // 1. Check Technical Firestore IDs
  if (rawSector === '7hxb9mxwp8e1dtp2suvi') return 'hospitality'; // Default for service in this context might be hospitality if it has rooms, but for now we look for keywords below
  if (rawSector === 'vd0ndssw0kd34zohnrtv') return 'food-beverage';
  if (rawSector === 'gaghwmsccfa3wfoqyhdtp') return 'retail';

  // 2. Check for Hospitality Keywords (Highest Priority within Service)
  const bizName = (business.name || business.business_name || '').toLowerCase();
  
  const isHospitality = category.includes('otel') || 
                       category.includes('konaklama') || 
                       category.includes('hospitality') ||
                       category.includes('turizm') ||
                       category.includes('seyahat') ||
                       bizName.includes('otel') ||
                       bizName.includes('hotel') ||
                       bizName.includes('pansiyon') ||
                       bizName.includes('konak') ||
                       rawSector === 'hospitality' ||
                       rawSector === 'hotel';
                       
  if (isHospitality) return 'hospitality';

  // 3. Map common sectors
  switch (rawSector) {
    case 'food-beverage':
    case 'food_beverage':
    case 'restaurant':
    case 'food':
    case 'bakery':
      return 'food-beverage';
    case 'retail':
    case 'supermarket':
    case 'shop':
      return 'retail';
    case 'service':
    case 'hizmet':
      return 'service';
    default:
      return 'food-beverage';
  }
};

// 🎯 SIDEBAR TYPE LOGIC
const determineSidebarType = (user, selectedBiz) => {
  if (!user?.id && !user?.email) return null;

  const currentPath = window.location.pathname;

  if (currentPath.startsWith('/dashboard/admin/')) return 'admin';
  if (currentPath.startsWith('/subscription/') || currentPath.startsWith('/brands/') ||
    currentPath.startsWith('/dashboard/personal') || 
    (currentPath.startsWith('/business/') && !currentPath.startsWith('/business/whatsapp'))) {
    return 'business';
  }

  if (selectedBiz && selectedBiz.id && selectedBiz.id !== 'business_account') {
    const normSector = getNormalizedSector(selectedBiz);
    
    let correctSidebarType;
    switch (normSector) {
      case 'hospitality':
        correctSidebarType = 'sectoral_hospitality';
        break;
      case 'retail':
        correctSidebarType = 'sectoral_retail';
        break;
      case 'service':
        correctSidebarType = 'sectoral_service';
        break;
      case 'food-beverage':
      default:
        correctSidebarType = 'sectoral_food-beverage';
    }

    safeStorage.setItem('sidebarType', correctSidebarType);
    return correctSidebarType;
  }

  if (!selectedBiz || !selectedBiz.id || selectedBiz.id === 'business_account') {
    return 'business';
  }

  return safeStorage.getItem('sidebarType') || 'business';
};

// 🎯 HEADER MODE LOGIC
const determineHeaderMode = (user) => {
  if (!user) return 'business';

  // Check for admin users
  if (user?.user_type === 'system_admin' || user?.user_type === 'admin' ||
    user?.role === 'super_admin' || user?.role === 'system_admin') {
    return 'admin';
  }

  // SaaS Unified: Default to business mode for regular users
  if (user?.type === 'human') return 'business';

  switch (user?.user_type) {
    case 'business_owner':
      return 'business';
    case 'brand_employee':
      return 'employee';
    default:
      return 'business';
  }
};

export const AuthBusinessProvider = ({ children }) => {
  const [state, setState] = useState({
    currentUser: JSON.parse(safeStorage.getItem('user') || 'null'),
    currentUserRole: safeStorage.getItem('userRole') || null,
    memberships: JSON.parse(safeStorage.getItem('user') || '{}').memberships || [],
    isAuthenticated: !!safeStorage.getItem('token'),
    token: safeStorage.getItem('token') || null,
    selectedBusiness: JSON.parse(safeStorage.getItem('selectedBusiness') || 'null'),
    showSidebar: determineSidebarVisibility(JSON.parse(safeStorage.getItem('user') || 'null'), JSON.parse(safeStorage.getItem('selectedBusiness') || 'null')),
    sidebarType: safeStorage.getItem('sidebarType') || 'business',
    headerMode: safeStorage.getItem('headerMode') || 'business',
    isLoading: !!safeStorage.getItem('token'),
    isCari: safeStorage.getItem('userRole') === 'cari'
  });

  // 🔄 HELPER: Metadata Fetching (Optimized Fast-Path)
  const fetchBusinessMetadata = async (m) => {
    try {
      const rawSector = m.sector || m.business_type || 'food_beverage';
      const sectorPath = rawSector.replace(/-/g, '_');
      const altSectorPath = rawSector.replace(/_/g, '-');
      
      const paths = [
        `businesses/${sectorPath}/businesses/${m.business_id}/settings/business_info`,
        `businesses/${altSectorPath}/businesses/${m.business_id}/settings/business_info`,
        `businesses/${sectorPath}/businesses/${m.business_id}/profile/config`,
        `businesses/${altSectorPath}/businesses/${m.business_id}/profile/config`,
        `businesses/${sectorPath}/businesses/${m.business_id}`,
        `businesses/${altSectorPath}/businesses/${m.business_id}`
      ];

      let business_name = m.business_name;
      let logo = m.logo || m.logo_url || null;
      let subscription = m.subscription || null;
      let add_ons = m.add_ons || [];

      let retrievedName = null;
      let retrievedLogo = null;
      let retrievedSubscription = null;
      let retrievedAddOns = null;

      for (const path of paths) {
        try {
          const snap = await getDoc(doc(db, path));
          if (snap.exists()) {
            const data = snap.data();
            const foundName = data.name || data.businessName || data.restaurantName || data.business_name || data.brandName;
            const foundLogo = data.logo || data.business_logo || data.logo_url || data.brandLogo;
            
            if (foundName && foundName !== 'İşletme' && !retrievedName) {
              retrievedName = foundName;
            }
            if (foundLogo && !retrievedLogo) {
              retrievedLogo = foundLogo;
            }
            if (data.subscription && data.subscription.active_modules && !retrievedSubscription) {
              retrievedSubscription = data.subscription;
            }
            if ((data.add_ons || data.subscription?.add_ons) && !retrievedAddOns) {
              retrievedAddOns = data.add_ons || data.subscription?.add_ons;
            }

            // Hızlı çıkış: Kritik veriler elde edildiyse diğer path'leri sorgulama
            if (retrievedName && retrievedLogo) {
              break;
            }
          }
        } catch (e) { /* silent */ }
      }

      return {
        ...m,
        name: retrievedName || business_name || 'İşletme',
        business_name: retrievedName || business_name || 'İşletme',
        logo: retrievedLogo || logo,
        subscription: retrievedSubscription || subscription,
        add_ons: retrievedAddOns || add_ons,
        displaySector: SECTOR_LOCALIZATION[m.sector] || m.sector,
        displayRole: m.role === 'cari' ? 'Cari Müşteri' : (ROLE_LOCALIZATION[m.role] || m.role),
        enrichedAt: Date.now()
      };
    } catch (err) {
      return m;
    }
  };

  // 🔄 LOAD INITIAL STATE FUNCTION (Optimized Fast-Start)
  const loadInitialState = useCallback(async (injectedUser = null) => {
    try {
      let token = safeStorage.getItem('token');
      let cachedUser = JSON.parse(safeStorage.getItem('user') || 'null');
      let cachedSelectedBiz = JSON.parse(safeStorage.getItem('selectedBusiness') || 'null');

      // 🛡️ OTURUM KURTARMA: Token bir şekilde silinmiş ama Firebase auth kullanıcısı veya cachedUser hala varsa kurtar
      if (!token && (injectedUser || auth.currentUser)) {
        try {
          const activeFbUser = injectedUser || auth.currentUser;
          token = await activeFbUser.getIdToken();
          if (token) {
            safeStorage.setItem('token', token);
            console.log('🔄 AuthBusinessContext: Auto-recovered token from Firebase user');
          }
        } catch (tokRecErr) {
          console.warn('⚠️ Token recovery from Firebase user failed:', tokRecErr?.message);
        }
      }

      if (!token && !injectedUser && !cachedUser) {
        setState(prev => ({ ...prev, isLoading: false, isAuthenticated: false }));
        return;
      }
      
      if (cachedUser && cachedUser.id) {
        if (process.env.NODE_ENV === 'development') console.log('⚡ AuthBusinessContext: Fast-Start Hydration...');
        
        setState(prev => ({
          ...prev,
          currentUser: cachedUser,
          currentUserRole: cachedUser.role || safeStorage.getItem('userRole'),
          memberships: cachedUser.memberships || [],
          isAuthenticated: true,
          selectedBusiness: cachedSelectedBiz,
          isLoading: false, // UI opens NOW
          userBusinesses: (cachedUser.memberships || []).map(m => ({
            ...m,
            id: m.business_id,
            name: m.business_name || m.name,
            logo: m.logo || m.logo_url || null
          }))
        }));

        // Hydration sırasında sanal markaları da arka planda yükle
        if (cachedSelectedBiz && cachedSelectedBiz.id && cachedSelectedBiz.id !== 'business_account') {
          api.get(`/api/brands/virtual/${cachedSelectedBiz.id}`)
            .then(res => {
              const brands = res.data?.brands || [];
              setState(prev => ({ ...prev, virtualBrands: brands }));
              console.log(`✅ Loaded ${brands.length} virtual brands on hydration`);
            })
            .catch(err => {
              // Sessiz fallback - ağ veya backend yoksa log kirliliği yapma
              console.debug('Virtual brands hydration note:', err?.message || err);
            });
        }
      }

      // 2. BACKGROUND VALIDATION & ENRICHMENT
      const firebaseUser = injectedUser || auth.currentUser;
      if (!firebaseUser && !cachedUser) {
        setState(prev => ({ ...prev, isLoading: false, isAuthenticated: false }));
        return;
      }

      // Check if we need enrichment (Cache is old or missing)
      const needsEnrichment = !cachedUser?.memberships?.[0]?.enrichedAt || 
                              (Date.now() - (cachedUser?.memberships?.[0]?.enrichedAt || 0)) > 3600000; // 1 Hour

      if (needsEnrichment && firebaseUser) {
        if (process.env.NODE_ENV === 'development') console.log('🔄 AuthBusinessContext: Background enrichment starting...');
        
        // Fetch fresh user doc for memberships
        let userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (!userDoc.exists() && firebaseUser.email) {
          try {
            const q = query(collection(db, 'users'), where('email', '==', firebaseUser.email));
            const snap = await getDocs(q);
            if (!snap.empty) userDoc = snap.docs[0];
          } catch (indexErr) {
            console.warn('⚠️ AuthBusinessContext: users email query failed (index building or missing):', indexErr.message);
            // Fallback: try direct document lookups
            const emailDoc = await getDoc(doc(db, 'users', firebaseUser.email));
            if (emailDoc.exists()) {
              userDoc = emailDoc;
            } else {
              const uId = firebaseUser.email.replace(/[@.]/g, '_');
              const bizAccDoc = await getDoc(doc(db, 'business_accounts', uId));
              if (bizAccDoc.exists()) {
                userDoc = bizAccDoc;
              }
            }
          }
        }

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const normalizedUser = {
            ...userData,
            user_type: userData?.type || userData?.user_type || userData?.role
          };

          // 🚨 HARD-CODED DEBUG FALLBACK for zarifzade
          const normalizedEmail = (normalizedUser.email || '').toLowerCase();
          if (normalizedEmail === 'zarifzade@prmanager.com' || normalizedEmail === 'zarifzade@gmail.com') {
            normalizedUser.role = 'cari';
            normalizedUser.user_type = 'cari';
            if (!normalizedUser.associatedCariId) normalizedUser.associatedCariId = '3IKMzIsgK1mNIbuFJPhx';
            if (!normalizedUser.businesses) normalizedUser.businesses = [];
            if (normalizedUser.businesses.length === 0) normalizedUser.businesses.push('business_fb_mkpfltqu_it85p');
          }

          let memberships = normalizedUser.memberships || [];
          const isCariUser = normalizedUser.role === 'cari' || normalizedUser.user_type === 'cari' || normalizedUser.associatedCariId;
          
          if (isCariUser && memberships.length === 0) {
            const targetBusinesses = normalizedUser.businesses || (normalizedUser.business_id ? [normalizedUser.business_id] : []) || (normalizedUser.associatedBusinessId ? [normalizedUser.associatedBusinessId] : []);
            if (targetBusinesses.length > 0) {
              memberships = targetBusinesses.map(bizId => ({
                business_id: bizId,
                name: normalizedUser.associatedCariName || normalizedUser.brandName || 'Toptan Müşteri Portalı',
                business_name: normalizedUser.associatedCariName || normalizedUser.brandName || 'Toptan Müşteri Portalı',
                logo: normalizedUser.brandLogo || normalizedUser.logo || null,
                role: 'cari',
                sector: normalizedUser.sector || 'food-beverage',
                tenant_id: normalizedUser.tenant_id || 'cari_tenant'
              }));
              normalizedUser.memberships = memberships;
            }
          }
          
          // Parallel background enrichment
          const enriched = await Promise.all(memberships.map(m => fetchBusinessMetadata(m)));
          
          const selectedBiz = JSON.parse(safeStorage.getItem('selectedBusiness') || 'null');
          const matchingMem = selectedBiz ? enriched.find(m => m.business_id === selectedBiz.id) : enriched[0];

          const updatedUser = { 
            ...normalizedUser, 
            id: firebaseUser.uid, 
            memberships: enriched,
            courierId: matchingMem?.courierId || null,
            staffId: matchingMem?.staffId || null
          };
          try {
            safeStorage.setItem('user', JSON.stringify(updatedUser));
          } catch (storageErr) {
            console.warn('⚠️ localStorage full, cleaning cache:', storageErr);
            Object.keys(localStorage).forEach(k => {
              if (k.startsWith('qr_cached_') || k.startsWith('temp_') || k.startsWith('offline_')) {
                safeStorage.removeItem(k);
              }
            });
            try {
              safeStorage.setItem('user', JSON.stringify(updatedUser));
            } catch (retryErr) {
              const minUser = { id: updatedUser.id, email: updatedUser.email, role: updatedUser.role, name: updatedUser.name };
              try { safeStorage.setItem('user', JSON.stringify(minUser)); } catch (e) {}
            }
          }
          if (updatedUser.courierId) safeStorage.setItem('courierId', updatedUser.courierId);
          else safeStorage.removeItem('courierId');

          setState(prev => {
            let updatedSelectedBiz = prev.selectedBusiness;
            if (prev.selectedBusiness) {
              const matchingEnrichedMem = enriched.find(m => m.business_id === prev.selectedBusiness.id);
              if (matchingEnrichedMem) {
                updatedSelectedBiz = {
                  ...prev.selectedBusiness,
                  name: matchingEnrichedMem.business_name || prev.selectedBusiness.name,
                  logo: matchingEnrichedMem.logo || prev.selectedBusiness.logo,
                  sector: matchingEnrichedMem.sector || prev.selectedBusiness.sector
                };
                safeStorage.setItem('selectedBusiness', JSON.stringify(updatedSelectedBiz));
                safeStorage.setItem('selectedBusinessId', updatedSelectedBiz.id);
              }
            }
            return {
              ...prev,
              currentUser: updatedUser,
              currentUserRole: updatedUser.role || safeStorage.getItem('userRole') || prev.currentUserRole,
              memberships: enriched,
              selectedBusiness: updatedSelectedBiz,
              userBusinesses: enriched.map(m => ({
                ...m,
                id: m.business_id,
                name: m.business_name || m.name,
                logo: m.logo || m.logo_url || null
              }))
            };
          });
        }
      }
    } catch (error) {
      console.error('❌ AuthBusinessContext Error:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const initRef = React.useRef(false);
  const currentUidRef = React.useRef(null);
  const fcmUnsubscribeRef = React.useRef(null);

  // 🛡️ ACTIVE AUTH LISTENER: Firebase auth durumu her değiştiğinde (auto-login, refresh vb.) senkronize et
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔔 AuthBusinessContext: onAuthStateChanged detected active user:', firebaseUser?.uid || 'null');
          }
          
          // UID değiştiyse veya henüz initialize edilmediyse yükle
          if (!initRef.current || currentUidRef.current !== firebaseUser.uid) {
            initRef.current = true;
            currentUidRef.current = firebaseUser.uid;
            await loadInitialState(firebaseUser); // Taze kullanıcıyı doğrudan içeri pasla
          }
        } else {
          console.log('🔔 AuthBusinessContext: onAuthStateChanged detected logout or no user');
          currentUidRef.current = null;
          initRef.current = false;
          
          // 🛡️ KRITIK: Firebase user olmasa bile localStorage token varsa oturumu koru
          // Backend JWT tabanlı login (signInWithEmailAndPassword kullanmayan) desteklenir
          const localToken = safeStorage.getItem('token');
          const localUser = JSON.parse(safeStorage.getItem('user') || '{}');
          
          if (localToken && (localUser?.id || localUser?.email)) {
            console.log('🔄 AuthBusinessContext: No Firebase user but localStorage token exists, loading from storage...');
            await loadInitialState(null); // Firebase user yoksa null geçiyoruz
          } else {
            // Gerçekten logout - state'i temizle ve loading'i kapat
            setState(prev => ({
              ...prev,
              currentUser: null,
              currentUserRole: null,
              isAuthenticated: false,
              token: null,
              selectedBusiness: null,
              userBusinesses: [],
              isLoading: false
            }));
          }
        }
      } catch (err) {
        console.error('❌ AuthBusinessContext: Auth state sync error:', err);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    });

    return () => unsubscribe();
    // Ensure onAuthStateChanged is only subscribed ONCE on mount
  }, [loadInitialState]);

  // 🔔 FCM AUTO-INIT (App load'da bildirimleri tazele)
  useEffect(() => {
    if (state.isAuthenticated && state.currentUser?.email) {
      const timer = setTimeout(async () => {
        try {
          const businessIds = (state.userBusinesses || []).map(b => 
            typeof b === 'string' ? b : b.id
          ).filter(Boolean);
          
          await initFCMForUser(state.currentUser.email, businessIds);
          console.log('✅ FCM: Bildirim tokeni otomatik güncellendi');

          // 🔔 Setup foreground notification listeners with user role
          if (fcmUnsubscribeRef.current) {
            fcmUnsubscribeRef.current();
          }
          
          fcmUnsubscribeRef.current = setupForegroundNotifications((notification) => {
            console.log('🔔 Foreground Notification Handled:', notification);
          }, state.currentUserRole);
        } catch (err) {
          console.warn('⚠️ FCM auto-init hatası:', err.message);
        }
      }, 1500); // 1.5 saniye sonra başlat
      return () => {
        clearTimeout(timer);
        if (fcmUnsubscribeRef.current) {
          fcmUnsubscribeRef.current();
          fcmUnsubscribeRef.current = null;
        }
      };
    }
  }, [state.isAuthenticated, state.currentUser?.email, state.userBusinesses, state.currentUserRole, auth.currentUser]);

  // 🎯 LISTEN FOR LOCALSTORAGE CHANGES (BusinessSwitcher sidebarType updates)
  useEffect(() => {
    const handleLocalStorageChange = () => {
      const newSidebarType = safeStorage.getItem('sidebarType');
      console.log('🎯 AuthBusinessContext - localStorage sidebarType changed to:', newSidebarType);

      setState(prev => ({
        ...prev,
        sidebarType: newSidebarType
      }));
    };

    // Listen for custom localStorage change events
    window.addEventListener('localStorageChange', handleLocalStorageChange);
    window.addEventListener('businessChanged', handleLocalStorageChange);

    return () => {
      window.removeEventListener('localStorageChange', handleLocalStorageChange);
      window.removeEventListener('businessChanged', handleLocalStorageChange);
    };
  }, []);

  // 🎯 LISTEN FOR URL CHANGES (Route navigation updates)
  useEffect(() => {
    const handlePathChange = () => {
      console.log('🎯 URL changed, updating sidebar type for:', window.location.pathname);

      setState(prev => {
        const newSidebarType = determineSidebarType(prev.currentUser, prev.selectedBusiness);
        if (newSidebarType !== prev.sidebarType) {
          console.log('🎯 SidebarType updated from', prev.sidebarType, 'to', newSidebarType);
          return {
            ...prev,
            sidebarType: newSidebarType
          };
        }
        return prev;
      });
    };

    window.addEventListener('popstate', handlePathChange);
    return () => window.removeEventListener('popstate', handlePathChange);
  }, []);

  // 📱 APP LIFECYCLE LISTENER (PWA, Android, Desktop Resume)
  useEffect(() => {
    const cleanup = initAppLifecycleListener(({ backgroundDuration }) => {
      // Arka plandan dönüldüğünde oturumu canlı tut
      const currentToken = safeStorage.getItem('token');
      if (currentToken) {
        console.log('🔄 Lifecycle Resume: Session preserved, duration:', Math.round(backgroundDuration / 1000), 's');
      }
    });
    return () => cleanup();
  }, []);

  // 🔧 BUSINESS SWITCH - Kalıcı değişim
  const switchBusiness = useCallback(async (businessOrId) => {
    console.log('🔄 Switching business:', businessOrId);

    try {
      let finalBusiness;
      
      // ID mi yoksa nesne mi kontrol et
      if (typeof businessOrId === 'string') {
        const user = JSON.parse(safeStorage.getItem('user') || '{}');
        const memberships = user.memberships || [];
        const m = memberships.find(mem => mem.business_id === businessOrId);
        
        if (m) {
          finalBusiness = {
            id: m.business_id,
            name: m.business_name || 'İşletmem',
            sector: m.sector,
            tenant_id: m.tenant_id,
            logo: m.logo || m.logo_url || null,
            role: m.role || 'cari'
          };
        } else if (businessOrId === 'business_account') {
          finalBusiness = { id: 'business_account', name: 'Hesap Yönetimi' };
        } else {
          console.error('❌ switchBusiness: Membership not found for ID:', businessOrId);
          return;
        }
      } else {
        finalBusiness = { ...businessOrId };
      }

      // 🎯 FRESH METADATA FETCH: Get the latest name and logo for the brand we're switching TO
      if (finalBusiness.id && finalBusiness.id !== 'business_account') {
        try {
          const sector = finalBusiness.sector || finalBusiness.business_type || 'food-beverage';
          const sectorPath = sector.replace(/-/g, '_');
          const altSectorPath = sector.replace(/_/g, '-');
          
          const paths = [
            `businesses/${sectorPath}/businesses/${finalBusiness.id}/settings/business_info`,
            `businesses/${altSectorPath}/businesses/${finalBusiness.id}/settings/business_info`,
            `businesses/7hXb9mxWP8e1dtP2suvi/businesses/${finalBusiness.id}/settings/business_info`,
            `businesses/VD0Ndssw0KD34ZoHnRTV/businesses/${finalBusiness.id}/settings/business_info`,
            `businesses/GaGHwMSCFa3wFOQyhdtP/businesses/${finalBusiness.id}/settings/business_info`,
            `businesses/${sectorPath}/businesses/${finalBusiness.id}/profile/config`,
            `businesses/${altSectorPath}/businesses/${finalBusiness.id}/profile/config`,
            `businesses/${sectorPath}/businesses/${finalBusiness.id}`,
            `businesses/${altSectorPath}/businesses/${finalBusiness.id}`,
            `businesses/7hXb9mxWP8e1dtP2suvi/businesses/${finalBusiness.id}`,
            `businesses/VD0Ndssw0KD34ZoHnRTV/businesses/${finalBusiness.id}`,
            `businesses/GaGHwMSCFa3wFOQyhdtP/businesses/${finalBusiness.id}`,
            `restaurants/${finalBusiness.id}`,
            `businesses/${finalBusiness.id}`
          ];

          let retrievedName = null;
          let retrievedLogo = null;
          let retrievedSubscription = null;

          for (const path of paths) {
            try {
              const snap = await getDoc(doc(db, path));
              if (snap.exists()) {
                const data = snap.data();
                const foundName = data.name || data.businessName || data.restaurantName || data.business_name || data.brandName;
                const foundLogo = data.logo || data.business_logo || data.logo_url || data.brandLogo;
                
                if (foundName && foundName !== 'İşletme' && !retrievedName) {
                  retrievedName = foundName;
                }
                if (foundLogo && !retrievedLogo) {
                  retrievedLogo = foundLogo;
                }
                if (data.subscription && data.subscription.active_modules && !retrievedSubscription) {
                  retrievedSubscription = data.subscription;
                }
                
                if (retrievedName && retrievedLogo && retrievedSubscription) break;
              }
            } catch(e) { /* silent */ }
          }

          if (retrievedName) {
            finalBusiness.name = retrievedName;
          }
          if (retrievedLogo) {
            finalBusiness.logo = retrievedLogo;
          }
          if (retrievedSubscription) {
            finalBusiness.subscription = retrievedSubscription;
          }
        } catch (e) {
          console.warn('⚠️ AuthBusinessContext: Could not refresh metadata on switch:', e.message);
        }
      }

      // 1. localStorage güncelle
      safeStorage.setItem('selectedBusiness', JSON.stringify(finalBusiness));
      safeStorage.setItem('selectedBusinessId', finalBusiness.id || 'business_account');

      // 2. State güncelle
      const sidebarType = determineSidebarType(state.currentUser, finalBusiness);
      console.log(`🎯 AuthBusinessContext: Normalized sidebarType to "${sidebarType}"`);

      // 3. Sanal markaları çek
      let virtualBrands = [];
      if (finalBusiness.id && finalBusiness.id !== 'business_account') {
        try {
          const res = await api.get(`/api/brands/virtual/${finalBusiness.id}`);
          virtualBrands = res.data?.brands || [];
          console.log(`✅ Loaded ${virtualBrands.length} virtual brands for business ${finalBusiness.id}`);
        } catch (error) {
          console.debug('Virtual brands load note:', error?.message || error);
        }
      }

      const user = JSON.parse(safeStorage.getItem('user') || '{}');
      const memberships = user.memberships || [];
      const currentMem = memberships.find(mem => mem.business_id === finalBusiness.id);

      setState(prev => {
        const updatedUser = prev.currentUser ? {
          ...prev.currentUser,
          role: currentMem?.role || prev.currentUser.role,
          courierId: currentMem?.courierId || null,
          staffId: currentMem?.staffId || null
        } : null;

        if (updatedUser) {
          try {
            safeStorage.setItem('user', JSON.stringify(updatedUser));
          } catch (storageErr) {
            console.warn('⚠️ localStorage full, cleaning cache:', storageErr);
            Object.keys(localStorage).forEach(k => {
              if (k.startsWith('qr_cached_') || k.startsWith('temp_') || k.startsWith('offline_')) {
                safeStorage.removeItem(k);
              }
            });
            try {
              safeStorage.setItem('user', JSON.stringify(updatedUser));
            } catch (retryErr) {
              const minUser = { id: updatedUser.id, email: updatedUser.email, role: updatedUser.role, name: updatedUser.name };
              try { safeStorage.setItem('user', JSON.stringify(minUser)); } catch (e) {}
            }
          }
          safeStorage.setItem('userRole', updatedUser.role || '');
          if (updatedUser.courierId) safeStorage.setItem('courierId', updatedUser.courierId);
          else safeStorage.removeItem('courierId');
        }

        return {
          ...prev,
          currentUser: updatedUser,
          currentUserRole: updatedUser?.role || prev.currentUserRole,
          selectedBusiness: finalBusiness,
          virtualBrands,
          userBusinesses: prev.userBusinesses,
          sidebarType,
          showSidebar: determineSidebarVisibility(updatedUser, finalBusiness)
        };
      });

      // 4. localStorage'daki eski sidebarType'ı da güncelle
      safeStorage.setItem('sidebarType', sidebarType);
      console.log(`💾 Updated localStorage sidebarType to: ${sidebarType}`);

      // 3. Route yönlendirme (SaaS-style business-scoped URLs)
      let targetPath;

      if (finalBusiness.id === 'business_account') {
        // Admin ise admin paneline, değilse kişisel dashboard'a
        const user = state.currentUser;
        const userRole = user?.role || user?.user_type;
        const isAdmin = userRole === 'admin' || userRole === 'super_admin' || userRole === 'system_admin' || user?.user_type === 'admin';
        
        targetPath = isAdmin ? '/dashboard/admin/' : '/dashboard/personal';
      } else {
        // İşletme seçildi - sektörüne göre dashboard'a yönlendir (Centralized Logic)
        const normSector = getNormalizedSector(finalBusiness);
        const isCariUser = state.currentUserRole === 'cari' || state.currentUser?.role === 'cari' || finalBusiness.role === 'cari';

        if (isCariUser) {
          targetPath = `/sectors/${normSector}/operations/distribution`;
        } else {
          switch (normSector) {
            case 'hospitality':
              targetPath = `/sectors/service/hospitality/dashboard`;
              break;
            case 'food-beverage':
              targetPath = `/sectors/food-beverage/dashboard`;
              break;
            case 'retail':
              targetPath = `/sectors/retail/dashboard`;
              break;
            case 'service':
              targetPath = `/sectors/service/dashboard`;
              break;
            default:
              targetPath = `/sectors/food-beverage/dashboard`;
          }
        }
      }

      console.log('📍 Navigating to SaaS-style URL:', targetPath);

      // Trigger custom navigation event for BusinessNavigationGuard
      window.dispatchEvent(new CustomEvent('businessSwitched', {
        detail: { business: finalBusiness, targetPath }
      }));

      // Trigger standard events for BusinessContext synchronization
      window.dispatchEvent(new Event('businessChanged'));
      window.dispatchEvent(new Event('localStorageChange'));

      // Don't use window.location.href as it reloads the page
      // Let the calling component handle navigation
      console.log('📍 AuthBusinessContext: Business switch completed, target:', targetPath);

    } catch (error) {
      console.error('❌ Business switch error:', error);
    }
  }, [state.currentUser]);

  // 🔧 REFRESH SELECTED BUSINESS - Detayları yeniden çek
  const refreshSelectedBusiness = useCallback(async () => {
    if (!state.selectedBusiness?.id) return;
    console.log('🔄 Refreshing selected business details...');
    await switchBusiness(state.selectedBusiness.id);
  }, [state.selectedBusiness, switchBusiness]);

  // 🔧 SELECT BUSINESS - Wrapper for business switching with navigation path return
  const selectBusiness = useCallback(async (business) => {
    console.log('🔄 selectBusiness called with:', business);

    // 🛡️ SECURITY CHECK: Prevent selecting invalid business IDs
    if (!business || !business.id || business.id === 'all') {
      console.warn('❌ Attempted to select invalid business ID:', business?.id);
      return null;
    }

    // Call switchBusiness to update state
    await switchBusiness(business);

    // Determine and return navigation path
    let targetPath;
    const normSector = getNormalizedSector(business);

    if (business.id === 'business_account' || business.type === 'business_account' || business.business_type === 'management') {
      targetPath = '/brands';
      return targetPath;
    }

    const isCariUser = state.currentUserRole === 'cari' || state.currentUser?.role === 'cari' || business.role === 'cari';

    if (isCariUser) {
      targetPath = `/sectors/${normSector}/operations/distribution`;
      return targetPath;
    }

    // SaaS-style URL routing
    switch (normSector) {
      case 'hospitality':
        targetPath = '/sectors/service/hospitality/dashboard';
        break;
      case 'food-beverage':
        targetPath = '/sectors/food-beverage/dashboard';
        break;
      case 'retail':
        targetPath = '/sectors/retail/dashboard';
        break;
      case 'service':
        targetPath = '/sectors/service/dashboard';
        break;
      default:
        targetPath = '/sectors/food-beverage/dashboard';
    }

    console.log('✅ selectBusiness completed, returning path:', targetPath);
    return targetPath;
  }, [switchBusiness]);

  // 🔧 LOGIN SUCCESS HANDLER
  const handleLoginSuccess = useCallback(async (userData, token) => {
    console.log('🔄 AuthBusinessContext: handleLoginSuccess Unified SaaS', { userData });

    const normalizedUser = {
      ...userData,
      user_type: userData?.type || userData?.user_type || userData?.role
    };

    let memberships = normalizedUser.memberships || [];

    // 🚨 HARD-CODED DEBUG FALLBACK for zarifzade
    const normalizedEmail = (normalizedUser.email || '').toLowerCase();
    if (normalizedEmail === 'zarifzade@prmanager.com' || normalizedEmail === 'zarifzade@gmail.com') {
      normalizedUser.role = 'cari';
      normalizedUser.user_type = 'cari';
      if (!normalizedUser.associatedCariId) normalizedUser.associatedCariId = '3IKMzIsgK1mNIbuFJPhx';
      if (!normalizedUser.businesses) normalizedUser.businesses = [];
      if (normalizedUser.businesses.length === 0) normalizedUser.businesses.push('business_fb_mkpfltqu_it85p');
      console.log('🛡️ AuthBusinessContext: zarifzade recovery applied for email:', normalizedEmail);
    }

    // 🛡️ CARI FIX: Cari kullanıcıları için 'businesses' dizisinden veya business_id'den sanal üyelikler oluştur
    const isCariUser = normalizedUser.role === 'cari' || normalizedUser.user_type === 'cari' || normalizedUser.associatedCariId;
    
    if (isCariUser && memberships.length === 0) {
      const targetBusinesses = normalizedUser.businesses || (normalizedUser.business_id ? [normalizedUser.business_id] : []) || (normalizedUser.associatedBusinessId ? [normalizedUser.associatedBusinessId] : []);


      if (targetBusinesses.length > 0) {
        if (process.env.NODE_ENV === 'development') console.log('🛡️ AuthBusinessContext: Generating virtual memberships for cari user during login from:', targetBusinesses);
        memberships = targetBusinesses.map(bizId => ({
          business_id: bizId,
          name: normalizedUser.associatedCariName || normalizedUser.brandName || 'Toptan Müşteri Portalı',
          business_name: normalizedUser.associatedCariName || normalizedUser.brandName || 'Toptan Müşteri Portalı',
          logo: normalizedUser.brandLogo || normalizedUser.logo || null,
          role: 'cari',
          sector: normalizedUser.sector || 'food-beverage',
          tenant_id: normalizedUser.tenant_id || 'cari_tenant'
        }));
        normalizedUser.memberships = memberships; // Sync back to user object
      }
    }
    let initialMembership = null;
    let initialSelectedBiz = null;

    // Auto-select logic for single membership (SKIP for owners - they need to choose between Management and POS)
    if (memberships.length === 1 && normalizedUser.user_type !== 'business_owner') {
      initialMembership = memberships[0];
      initialSelectedBiz = {
        id: initialMembership.business_id,
        name: initialMembership.business_name || 'İşletmem',
        sector: initialMembership.sector,
        tenant_id: initialMembership.tenant_id
      };
      safeStorage.setItem('selectedBusiness', JSON.stringify(initialSelectedBiz));
      safeStorage.setItem('selectedBusinessId', initialMembership.business_id);
    } else {
      // Çoklu işletme varsa veya Owner ise eski seçimi temizle ki seçim modalı açılsın
      safeStorage.removeItem('selectedBusiness');
      safeStorage.removeItem('selectedBusinessId');
    }

    const isAdmin = isAdminUser(normalizedUser);

    const isCari = normalizedUser.role === 'cari' || normalizedUser.user_type === 'cari';
    const hasCariBusinesses = isCari && normalizedUser.businesses && normalizedUser.businesses.length > 0;

    // 🎯 REDIRECT IF NO BUSINESS: Login anında hiç işletme yoksa personal dashboard'a git (Adminler ve CARI'ler hariç!)
    if (memberships.length === 0 && !isAdmin && !hasCariBusinesses) {
      console.log('🚀 AuthBusinessContext: No businesses on login, redirecting to personal dashboard');
      safeStorage.setItem('user', JSON.stringify(normalizedUser));
      safeStorage.setItem('token', token);
      window.location.href = '/dashboard/personal';
      return;
    }

    if (isAdmin) {
       console.log('🎯 AuthBusinessContext: Admin detected on login, skipping business-only redirects');
    }

    // 🎯 FAST LOGIN: Save immediately and defer heavy enrichment
    const initialUser = {
      ...normalizedUser,
      role: isAdmin ? (normalizedUser.role || 'admin') : (initialMembership?.role || normalizedUser.role || normalizedUser.user_type),
      associatedCariId: initialMembership?.associatedCariId || normalizedUser.associatedCariId,
      courierId: initialMembership?.courierId || normalizedUser.courierId || null,
      staffId: initialMembership?.staffId || normalizedUser.staffId || null,
      memberships: memberships
    };

    // Storage updates (Sync)
    safeStorage.setItem('user', JSON.stringify(initialUser));
    safeStorage.setItem('token', token);
    safeStorage.setItem('loginTime', Date.now().toString());
    safeStorage.setItem('userRole', initialUser.role || '');
    if (initialUser.associatedCariId) safeStorage.setItem('associatedCariId', initialUser.associatedCariId);
    if (initialUser.courierId) safeStorage.setItem('courierId', initialUser.courierId);
    else safeStorage.removeItem('courierId');

    // 📱 Cihaz ve Platform Bilgisi Kaydı
    try {
      const platformInfo = getPlatformInfo();
      const deviceId = getOrCreateDeviceId();
      safeStorage.setItem('sectorpilot_device_id', deviceId);
      safeStorage.setItem('sectorpilot_platform', platformInfo.platformType);
      console.log(`📱 Device registered: ${deviceId} (${platformInfo.platformType})`);
    } catch (dErr) {}

    // Initial state update (Instant)
    setState(prev => ({
      ...prev,
      currentUser: initialUser,
      currentUserRole: initialUser.role,
      memberships: memberships,
      selectedMembership: initialMembership,
      isAuthenticated: true,
      token,
      selectedBusiness: initialSelectedBiz,
      isLoading: false,
      userBusinesses: memberships.map(m => ({
        ...m,
        id: m.business_id,
        name: m.business_name || m.name,
        logo: m.logo || m.logo_url || null
      }))
    }));

    // Background Enrichment (Non-blocking)
    const startEnrichment = async () => {
      try {
        const enriched = await Promise.all(memberships.map(m => fetchBusinessMetadata(m)));
        const finalUser = { ...initialUser, memberships: enriched };
        safeStorage.setItem('user', JSON.stringify(finalUser));
        
        setState(prev => {
          let updatedSelectedBiz = prev.selectedBusiness;
          if (prev.selectedBusiness) {
            const matchingEnrichedMem = enriched.find(m => m.business_id === prev.selectedBusiness.id);
            if (matchingEnrichedMem) {
              updatedSelectedBiz = {
                ...prev.selectedBusiness,
                name: matchingEnrichedMem.business_name || prev.selectedBusiness.name,
                logo: matchingEnrichedMem.logo || prev.selectedBusiness.logo,
                sector: matchingEnrichedMem.sector || prev.selectedBusiness.sector
              };
              safeStorage.setItem('selectedBusiness', JSON.stringify(updatedSelectedBiz));
              safeStorage.setItem('selectedBusinessId', updatedSelectedBiz.id);
            }
          }
          return {
            ...prev,
            currentUser: finalUser,
            memberships: enriched,
            selectedBusiness: updatedSelectedBiz,
            userBusinesses: enriched.map(m => ({
              ...m,
              id: m.business_id,
              name: m.business_name || m.name,
              logo: m.logo || m.logo_url || null
            }))
          };
        });
      } catch (e) { console.warn('Background enrichment failed:', e); }
    };
    startEnrichment();

    window.dispatchEvent(new Event('authStateChanged'));
    window.dispatchEvent(new Event('localStorageChange'));

    // 🔔 FCM Init
    if (memberships.length > 0) {
      setTimeout(() => {
        const bizIds = memberships.map(m => m.business_id).filter(id => id && id !== 'all');
        initFCMForUser(normalizedUser.email, bizIds).catch(e => console.warn('FCM fail:', e));
      }, 2000);
    }

  }, []);

  // 🔧 LOGOUT HANDLER
  const logout = useCallback(async () => {
    console.log('🔄 AuthBusinessContext - Logout');

    // 🔔 FCM Token'ı temizle (bu cihaza artık bildirim gelmesin)
    try {
      const savedUser = JSON.parse(safeStorage.getItem('user') || '{}');
      if (savedUser?.email) {
        await removeFCMToken(savedUser.email);
      }
    } catch (fcmErr) {
      console.warn('⚠️ FCM token temizleme hatası:', fcmErr);
    }

    // 🛡️ Firebase Logout: Gerçek çıkış işlemi
    try {
      const { signOut } = await import('firebase/auth');
      await signOut(auth);
      console.log('✅ AuthBusinessContext: Firebase signOut success');
    } catch (signOutErr) {
      console.error('❌ AuthBusinessContext: Firebase signOut error:', signOutErr);
    }

    // Clear all storage
    ['user', 'token', 'selectedBusiness', 'selectedBusinessId', 'userRole', 'loginTime', 'associatedCariId'].forEach(k => safeStorage.removeItem(k));
    
    // Çalışan session'ı temizle
    sessionStorage.removeItem('staff_user');
    sessionStorage.removeItem('staff_token');

    setState({
      currentUser: null,
      currentUserRole: null,
      isAuthenticated: false,
      token: null,
      userBusinesses: [],
      selectedBusiness: null,
      virtualBrands: [],
      showSidebar: false,
      sidebarType: null,
      headerMode: 'business',
      isLoading: false
    });

    // 🚀 SMART REDIRECT: Determine where to go based on domain
    const hostname = window.location.hostname;
    const systemDomains = ['sectoras.com', 'localhost', 'prmanager-f881b.web.app', 'sectoras-app.web.app'];
    const isSystemDomain = systemDomains.some(d => hostname === d || hostname.endsWith('.' + d));

    if (isSystemDomain) {
      window.location.href = '/login';
    } else {
      // Branded domain - redirect to root (which is the menu)
      window.location.href = '/';
    }
  }, []);

  // 🔧 REFRESH USER DATA
  const refreshUserData = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));

      // Reload from localStorage for now
      await loadInitialState();

    } catch (error) {
      console.error('❌ Refresh user data error:', error);
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [loadInitialState]);

  // 🔧 CHECK SUBSCRIPTION ACCESS
  const checkSubscriptionAccess = useCallback(async () => {
    if (!state.currentUser?.email) return false;

    try {
      const subscriptionStatus = await UserManagementService.checkSubscriptionStatus(state.currentUser.email);

      setState(prev => ({
        ...prev,
        subscriptionStatus
      }));

      return subscriptionStatus.isActive;
    } catch (error) {
      console.error('❌ Subscription check error:', error);
      return false;
    }
  }, [state.currentUser?.email]);

  // 🔧 CREATE NEW BUSINESS
  const createBusiness = useCallback(async (businessData) => {
    if (!state.currentUser?.email) {
      throw new Error('User not authenticated');
    }

    // This would integrate with RestaurantFactoryService
    // For now, just a placeholder
    console.log('🏪 Creating business:', businessData);

    // Refresh user data after creation
    await refreshUserData();
  }, [state.currentUser?.email, refreshUserData]);

  const value = React.useMemo(() => ({
    // State
    ...state,

    // Actions
    switchBusiness,
    selectBusiness,
    refreshSelectedBusiness,
    handleLoginSuccess,
    logout,
    refreshUserData,
    loadInitialState,
    checkSubscriptionAccess,
    createBusiness
  }), [
    state,
    switchBusiness,
    selectBusiness,
    refreshSelectedBusiness,
    handleLoginSuccess,
    logout,
    refreshUserData,
    loadInitialState,
    checkSubscriptionAccess,
    createBusiness
  ]);

  return (
    <AuthBusinessContext.Provider value={value}>
      {children}
    </AuthBusinessContext.Provider>
  );
};